import { FC } from 'react'
import { SectionEmpty } from './Section'
import { RunAttitudeRow } from './methodologyDiff'
import { formatFixed } from './format'

export type RunAttitudesProps = {
  rows: RunAttitudeRow[]
}

type Col = { header: string; render: (r: RunAttitudeRow) => string; left?: boolean }

// Only render attitude columns that at least one row actually populates, since the
// relevant set differs by series (e.g. frh/rrh/speed vs yaw/steer/roll).
const allColumns: { key: keyof RunAttitudeRow; header: string; digits: number }[] = [
  { key: 'frh', header: 'FRH', digits: 3 },
  { key: 'rrh', header: 'RRH', digits: 3 },
  { key: 'yaw', header: 'YAW', digits: 3 },
  { key: 'steer', header: 'Steer', digits: 3 },
  { key: 'roll', header: 'Roll', digits: 3 },
  { key: 'speed', header: 'Speed', digits: 1 }
]

const RunAttitudes: FC<RunAttitudesProps> = ({ rows }) => {
  if (!rows.length) return <SectionEmpty message="No cases on the selected run." />

  const active = allColumns.filter(c => rows.some(r => typeof r[c.key] === 'number'))
  const columns: Col[] = [
    { header: 'Case', render: r => r.caseName, left: true },
    ...active.map(c => ({
      header: c.header,
      render: (r: RunAttitudeRow) => formatFixed(r[c.key] as number | undefined, c.digits)
    }))
  ]

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-50 dark:bg-neutral-70">
            {columns.map(col => (
              <th
                key={col.header}
                className={`border border-gray-200 dark:border-neutral-60 px-2 py-1 font-bold ${
                  col.left ? 'text-left' : 'text-right'
                }`}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.caseName}-${i}`}>
              {columns.map(col => (
                <td
                  key={col.header}
                  className={`border border-gray-200 dark:border-neutral-60 px-2 py-1 ${
                    col.left ? 'text-left' : 'text-right tabular-nums'
                  }`}>
                  {col.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default RunAttitudes
